<template>
  <div id="app" :class="this.$mq">
    <transition name="fade-slow">
      <loader v-if="loading" :full="true" class="xs-hide md-hide z4"></loader>
    </transition>
    <notice text="This site uses Google Analytics. By continuing, you accept the use of cookies." v-if="displayNotice()" @click.native="acceptCookies()"></notice>
    <router-view/>
    <router-view name="left" class="z4"></router-view>
    <router-view name="right" class="z1"></router-view>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import { unSlugify } from '@/utils'
import Notice from '@/components/Notice'
import Loader from '@/components/Loader'

export default {
  name: 'App',
  components: {
    Notice,
    Loader
  },
  computed: {
    ...mapState([
      'currentVideo',
      'loading'
    ])
  },
  methods: {
    displayNotice: function () {
      if (this.$route.name === 'VideoEmbed' || this.$route.name === 'VimeoVideoEmbed') {
        return false
      }
      if (this.$cookie.get('soe-tv-accept-cookies')) {
        return false
      } else {
        return true
      }
    },
    acceptCookies: function () {
      this.hideNotice = true
      this.$cookie.set('soe-tv-accept-cookies', '1', 365)
      document.getElementById('notice').classList.add('hidden')
    },
    pageTitle: function () {
      if (this.$route.name === 'Video' ||
        (this.$route.hash && this.currentVideo) ||
        (this.$route.slug && this.currentVideo)) {
        return {
          inner: this.currentVideo.title
        }
      } else if (this.$route.name === 'Videos') {
        if (this.$route.params.slug) {
          return {
            inner: this.currentVideo.title
          }
        } else {
          return {
            inner: 'All Videos'
          }
        }
      } else if (this.$route.name === 'Channels') {
        if (this.$route.params.slug) {
          return {
            inner: unSlugify(this.$route.params.slug)
          }
        } else {
          return {
            inner: 'Channels'
          }
        }
      } else if (this.$route.name === 'Subchannel') {
        if (this.$route.params.subChannelSlug) {
          return {
            inner: unSlugify(this.$route.params.subChannelSlug)
          }
        }
      } else if (this.$route.name === 'Themes') {
        if (this.$route.params.themeSlug !== undefined) {
          return {
            inner: unSlugify(this.$route.params.themeSlug)
          }
        } else {
          return {
            inner: 'Themes'
          }
        }
      } else {
        return {
          inner: this.$route.name
        }
      }
    },
    pageImage: function (playButton) {
      if (this.currentVideo) {
        if (playButton) {
          let srcRoot = 'https://i.vimeocdn.com/filter/overlay'
          let src0 = encodeURIComponent(this.currentVideo.image_large)
          let src1 = 'https%3A%2F%2Ff.vimeocdn.com%2Fimages_v6%2Fshare%2Fplay_icon_overlay.png'
          return `${srcRoot}?src0=${src0}&src1=${src1}`
        } else {
          return this.currentVideo.image_large
        }
      } else {
        return 'https://soe.tv/static/soetv.jpg'
      }
    },
    pageDescription: function () {
      if (this.currentVideo) {
        return this.currentVideo.description
      } else {
        return 'SOE.TV is a platform for moving images by Studio Olafur Eliasson, Berlin. The site offers glimpses into the experiment-based, research-driven work at the studio and the activities of SOE Kitchen, as well as documentation of Eliasson’s artworks, architectural projects, and exhibitions.'
      }
    },
    getCurrentVideoUrl (player) {
      if (this.currentVideo !== null) {
        if (player) {
          return this.currentVideo.url.replace('//vimeo.com/', '//player.vimeo.com/video/')
        } else {
          return this.currentVideo.url
        }
      } else {
        return 'undefined'
      }
    }
  },

  head: {
    meta: function () {
      let meta = []
      meta = [
        {
          p: 'og:title',
          c: 'SOE.TV | ' + this.pageTitle().inner,
          id: 'og_title'
        },
        {
          p: 'twitter:title',
          c: 'SOE.TV | ' + this.pageTitle().inner,
          id: 'tw_title'
        },
        {
          p: 'og:image',
          c: this.pageImage(true),
          id: 'og_img'
        },
        {
          p: 'twitter:image',
          c: this.pageImage(),
          id: 'tw_img'
        },
        {
          p: 'og:image:width',
          c: 1920,
          id: 'og_imge_w'
        },
        {
          p: 'og:image:height',
          c: 1080,
          id: 'og_img_h'
        },
        {
          p: 'og:type',
          c: 'video',
          id: 'og_type'
        },
        {
          p: 'og:url',
          c: window.location.href,
          id: 'og_url'
        },
        {
          p: 'twitter:description',
          c: this.pageDescription(),
          id: 'tw_desc'
        },
        {
          p: 'og:description',
          c: this.pageDescription(),
          id: 'og_desc'
        },
        {
          itemprop: 'name',
          content: 'SOE.TV | ' + this.pageTitle().inner,
          id: 'iname'
        },
        {
          itemprop: 'description',
          content: this.pageDescription(),
          id: 'idesc'
        },
        {
          name: 'description',
          content: this.pageDescription(),
          id: 'desc'
        },
        {
          p: 'og:video:url',
          c: this.getCurrentVideoUrl(true),
          id: 'og_vid_url'
        },
        {
          p: 'og:video:secure_url',
          c: this.getCurrentVideoUrl(true),
          id: 'og_vid_sec_url'
        },
        {
          p: 'twitter:card',
          c: 'player',
          id: 'tw_card'
        },
        {
          p: 'twitter:player',
          c: this.getCurrentVideoUrl(true),
          id: 'tw_pl'
        },
        {
          p: 'twitter:player:width',
          c: 1280,
          id: 'tw_p_w'
        },
        {
          p: 'twitter:player:height',
          c: 720,
          id: 'tw_p_h'
        },
        {
          p: 'twitter:site',
          c: '@vimeo',
          id: 'tw_s'
        },
        {
          p: 'og:video:type',
          c: 'text/html',
          id: 'og_vid_type'
        }
      ]

      return meta
    },
    title: function () {
      if (this.pageTitle().inner !== null) {
        return this.pageTitle()
      } else {
        return {
          inner: 'Loading...'
        }
      }
    }
  },

  watch: {
    $route: function () {
      this.$emit('updateHead')
    },
    currentVideo: function () {
      var self = this
      window.setTimeout(function () {
        self.$emit('updateHead')
      }, 3000)
    }
  }

}
</script>

<style>
@import 'style/app';
</style>



// WEBPACK FOOTER //
// src/App.vue